import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, ArrowLeft, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { SEO } from "@/components/SEO";

const Articles = () => {
  const { data: articles, isLoading, isError, error } = useQuery({
    queryKey: ['public-articles'],
    queryFn: async (): Promise<any[]> => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  return (
    <Layout>
      <SEO 
        title="מאמרים ומדריכים"
        description="מדריכים מקצועיים, חדשות ועדכונים מעולם המערכות הדיגיטליות וה-AI."
      />
      
      <div className="bg-white min-h-screen pb-20">
        <div className="pt-32 pb-16 border-b border-slate-100">
          <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-4">
              הבלוג שלנו
            </h1>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              תובנות, מדריכים וחדשות מעולם ה-AI והמערכות הדיגיטליות.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 max-w-6xl py-16">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-video w-full rounded-2xl" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="text-center py-20 bg-red-50 rounded-2xl border border-red-100">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-red-900">שגיאה בטעינת המאמרים</h3>
              <p className="text-red-700 mt-1">לא ניתן לטעון את המאמרים כרגע. אנא נסה שוב מאוחר יותר.</p>
              <p className="text-xs text-red-500 mt-4 font-mono ltr">{(error as any)?.message}</p>
            </div>
          ) : articles?.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              טרם פורסמו מאמרים.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles?.map((article: any) => (
                <Link key={article.id} to={`/articles/${article.slug}`} className="group block h-full">
                  <Card className="h-full border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col">
                    <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                      {article.cover_image ? (
                        <img 
                          src={article.cover_image} 
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50">
                          <span className="text-4xl font-black opacity-10">BLOG</span>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(article.created_at), 'dd/MM/yyyy')}
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-blue-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 mb-6 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center text-blue-600 text-sm font-bold mt-auto">
                        קרא עוד
                        <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Articles;